import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, CreditCard, Truck, MapPin, Loader2 } from "lucide-react";

const VEHICLES = [
  {
    key: "passat",
    name: "Volkswagen Passat",
    upcharge: 0,
    image: "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=900&q=80",
    blurb: "Standard. Comfortable for 1–2 small or medium pets.",
  },
  {
    key: "eclass",
    name: "Mercedes E-Class",
    upcharge: 100,
    image: "https://images.unsplash.com/photo-1711716854806-189ae6524a99?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    blurb: "Premium ride. Smooth and quiet — ideal for nervous pets.",
  },
  {
    key: "vclass",
    name: "Mercedes V-Class Minibus",
    upcharge: 200,
    image: "https://images.pexels.com/photos/17455633/pexels-photo-17455633.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    blurb: "Spacious. Best for multiple pets or large breeds.",
  },
];

function calcTotal(miles, vehicle, crossesChannel = false) {
  if (!miles) return 0;
  let base = miles <= 500 ? miles * 2.8 : 500 * 2.8 + (miles - 500) * 1.7;
  if (crossesChannel && base < 240) base = 240;
  const up = VEHICLES.find((v) => v.key === vehicle)?.upcharge || 0;
  return Math.round((base + up) * 100) / 100;
}

const STEPS = ["Trip", "Vehicle", "Pet", "Review & Pay"];

export default function BookingWizard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialChannel = searchParams.get("channel") === "1";
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [routeInfo, setRouteInfo] = useState(initialChannel ? { crosses_channel: true, _fromUrl: true } : null);
  const [trip, setTrip] = useState({
    pickup_location: searchParams.get("pickup") || "",
    dropoff_location: searchParams.get("dropoff") || "",
    distance_miles: searchParams.get("miles") || "",
    pickup_date: "",
  });
  const [vehicle, setVehicle] = useState(searchParams.get("vehicle") || "passat");
  const [pet, setPet] = useState({ pet_name: "", pet_type: "Dog", pet_breed: "", pet_weight_kg: "", special_notes: "", num_passengers: 1, num_pets: 1 });

  useEffect(() => {
    if (user === null) nav("/login", { state: { next: "/book" + (searchParams.toString() ? "?" + searchParams.toString() : "") } });
  }, [user, nav, searchParams]);

  const total = useMemo(() => calcTotal(parseFloat(trip.distance_miles || 0), vehicle, !!routeInfo?.crosses_channel), [trip.distance_miles, vehicle, routeInfo]);
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const calculateDistance = async () => {
    if (!trip.pickup_location || !trip.dropoff_location) {
      toast.error("Enter both pickup and drop-off locations first");
      return;
    }
    setCalculating(true);
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const { data } = await api.post("/geocode/distance", {
          pickup_address: trip.pickup_location,
          dropoff_address: trip.dropoff_location,
        });
        setTrip((t) => ({ ...t, distance_miles: String(data.distance_miles) }));
        setRouteInfo(data);
        toast.success(`Distance: ${data.distance_miles} miles`);
        setCalculating(false);
        return;
      } catch (e) {
        lastErr = e;
        const status = e.response?.status;
        if (status && status >= 400 && status < 500) break;
        if (attempt < 3) await new Promise((r) => setTimeout(r, 1200 * attempt));
      }
    }
    setCalculating(false);
    const status = lastErr?.response?.status;
    if (!status || status >= 500) {
      toast.error("Our server is warming up — please tap Auto again in a few seconds.");
    } else {
      toast.error(formatApiError(lastErr?.response?.data?.detail) || "Could not calculate distance");
    }
  };

  const validateStep = () => {
    if (step === 0) {
      if (!trip.pickup_location || !trip.dropoff_location || !trip.distance_miles || !trip.pickup_date) {
        toast.error("Please fill all trip details"); return false;
      }
      if (parseFloat(trip.distance_miles) <= 0) {
        toast.error("Distance must be greater than 0"); return false;
      }
    }
    if (step === 2 && !pet.pet_name) { toast.error("Please enter your pet's name"); return false; }
    return true;
  };

  const handleNext = () => { if (validateStep()) next(); };

  const submit = async () => {
    if (!validateStep()) return;
    setCreating(true);
    try {
      const payload = {
        ...trip,
        ...pet,
        distance_miles: parseFloat(trip.distance_miles),
        pet_weight_kg: pet.pet_weight_kg ? parseFloat(pet.pet_weight_kg) : null,
        num_pets: parseInt(pet.num_pets || 1, 10),
        num_passengers: parseInt(pet.num_passengers || 1, 10),
        vehicle,
        crosses_channel: !!routeInfo?.crosses_channel,
      };
      const { data: booking } = await api.post("/bookings", payload);
      // Initiate Stripe
      const { data: checkout } = await api.post(`/bookings/${booking.id}/checkout`, {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setCreating(false);
    }
  };

  if (user === undefined) return <div className="p-12 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="booking-wizard">
      <h1 className="font-display text-4xl md:text-5xl tracking-tight">Book your pet transport</h1>
      <p className="text-muted-foreground mt-3">Four quick steps. Pay securely with Stripe at the end.</p>

      <div className="mt-10 mb-8">
        <Progress value={((step + 1) / STEPS.length) * 100} />
        <div className="flex justify-between mt-3 text-xs uppercase tracking-wider text-muted-foreground">
          {STEPS.map((s, i) => (
            <span key={s} className={i === step ? "text-foreground font-semibold" : ""}>{i + 1}. {s}</span>
          ))}
        </div>
      </div>

      <Card className="p-8 md:p-10 border-border">
        {step === 0 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h2 className="font-display text-2xl">Trip details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Pickup location</Label>
                <AddressAutocomplete
                  value={trip.pickup_location}
                  onChange={(v) => setTrip({ ...trip, pickup_location: v })}
                  placeholder="London, UK"
                  testId="wizard-pickup"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Drop-off location</Label>
                <AddressAutocomplete
                  value={trip.dropoff_location}
                  onChange={(v) => setTrip({ ...trip, dropoff_location: v })}
                  placeholder="Edinburgh, UK"
                  testId="wizard-dropoff"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Distance (miles)</Label>
                <div className="flex gap-2">
                  <Input type="number" min="1" step="0.1" value={trip.distance_miles} onChange={(e) => setTrip({ ...trip, distance_miles: e.target.value })} placeholder="405" data-testid="wizard-miles-input" />
                  <Button type="button" variant="outline" onClick={calculateDistance} disabled={calculating} className="shrink-0" data-testid="wizard-calc-distance-btn" title="Calculate from addresses">
                    {calculating ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />}
                  </Button>
                </div>
                {routeInfo?.pickup && routeInfo?.dropoff && (
                  <p className="text-xs text-muted-foreground mt-2" data-testid="wizard-route-info">
                    {routeInfo.pickup.display_name.split(",").slice(0, 2).join(",")} → {routeInfo.dropoff.display_name.split(",").slice(0, 2).join(",")} · {routeInfo.distance_km} km
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Pickup date</Label>
                <Input type="date" value={trip.pickup_date} onChange={(e) => setTrip({ ...trip, pickup_date: e.target.value })} data-testid="wizard-date-input" />
              </div>
            </div>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h2 className="font-display text-2xl">Choose your vehicle</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {VEHICLES.map((v) => {
                const t = calcTotal(parseFloat(trip.distance_miles || 0), v.key, !!routeInfo?.crosses_channel);
                const selected = vehicle === v.key;
                return (
                  <button
                    type="button"
                    key={v.key}
                    onClick={() => setVehicle(v.key)}
                    className={`text-left rounded-lg border p-5 transition-all bg-card ${selected ? "border-primary ring-2 ring-primary/30" : "border-border hover:border-primary/60"}`}
                    data-testid={`wizard-vehicle-${v.key}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden rounded-md mb-4 bg-muted">
                      <img src={v.image} alt={v.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display text-lg leading-tight">{v.name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">{v.tier}</p>
                      </div>
                      {selected && <Check size={18} className="text-primary mt-1" />}
                    </div>
                    <p className="text-sm text-muted-foreground mt-3">{v.blurb}</p>
                    <p className="font-display text-2xl mt-4 font-medium">£{t.toFixed(2)}</p>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <h2 className="font-display text-2xl">About your pet</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Pet name</Label>
                <Input value={pet.pet_name} onChange={(e) => setPet({ ...pet, pet_name: e.target.value })} data-testid="wizard-pet-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Pet type</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={pet.pet_type}
                  onChange={(e) => setPet({ ...pet, pet_type: e.target.value })}
                  data-testid="wizard-pet-type"
                >
                  <option value="Dog">Dog</option>
                  <option value="Cat">Cat</option>
                  <option value="Ferret">Ferret</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Breed (optional)</Label>
                <Input value={pet.pet_breed} onChange={(e) => setPet({ ...pet, pet_breed: e.target.value })} data-testid="wizard-pet-breed" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Weight (kg)</Label>
                <Input type="number" step="0.5" value={pet.pet_weight_kg} onChange={(e) => setPet({ ...pet, pet_weight_kg: e.target.value })} data-testid="wizard-pet-weight" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Number of pets</Label>
                <Input
                  type="number"
                  min="1"
                  max="6"
                  value={pet.num_pets}
                  onChange={(e) => setPet({ ...pet, num_pets: parseInt(e.target.value || "1", 10) })}
                  data-testid="wizard-num-pets"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Number of passengers</Label>
                <Input
                  type="number"
                  min="1"
                  max="6"
                  value={pet.num_passengers}
                  onChange={(e) => setPet({ ...pet, num_passengers: parseInt(e.target.value || "1", 10) })}
                  data-testid="wizard-num-passengers"
                />
                <p className="text-[11px] text-muted-foreground mt-1">People travelling with your pet</p>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Special notes (optional)</Label>
              <Textarea rows={3} value={pet.special_notes} onChange={(e) => setPet({ ...pet, special_notes: e.target.value })} placeholder="Travel anxiety, medication, crate provided..." data-testid="wizard-pet-notes" />
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <h2 className="font-display text-2xl">Review &amp; pay</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="p-5 rounded-lg bg-secondary/60 border border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Route</p>
                <p className="mt-2 font-medium">{trip.pickup_location} → {trip.dropoff_location}</p>
                <p className="text-muted-foreground">{trip.distance_miles} miles · {trip.pickup_date}</p>
              </div>
              <div className="p-5 rounded-lg bg-secondary/60 border border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Vehicle</p>
                <p className="mt-2 font-medium">{VEHICLES.find((v) => v.key === vehicle).name}</p>
                <p className="text-muted-foreground">{VEHICLES.find((v) => v.key === vehicle).tier}</p>
              </div>
              <div className="p-5 rounded-lg bg-secondary/60 border border-border md:col-span-2">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Pet &amp; passengers</p>
                <p className="mt-2 font-medium">
                  {pet.pet_name} · {pet.pet_type}{pet.pet_breed && ` (${pet.pet_breed})`}
                  {pet.num_pets > 1 && ` · ${pet.num_pets} pets total`}
                </p>
                <p className="text-muted-foreground text-sm mt-1">
                  {pet.num_passengers} passenger{pet.num_passengers > 1 ? "s" : ""} travelling
                </p>
                {pet.special_notes && <p className="text-muted-foreground mt-2 text-sm">{pet.special_notes}</p>}
              </div>
            </div>

            <div className="rounded-lg border border-primary/30 bg-primary/5 p-6 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Total</p>
                <p className="font-display text-4xl font-medium" data-testid="wizard-total">£{total.toFixed(2)}</p>
                {routeInfo?.crosses_channel && (
                  <p className="text-xs text-accent mt-1">+ Eurotunnel (paid separately)</p>
                )}
              </div>
              <Truck className="text-primary" size={44} strokeWidth={1.2} />
            </div>

            {routeInfo?.crosses_channel && (
              <div className="rounded-md bg-accent/10 border border-accent/40 p-4 flex items-start gap-3" data-testid="wizard-review-eurotunnel">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-accent text-accent-foreground shrink-0">+ Eurotunnel</span>
                <div className="text-sm">
                  <p className="font-medium text-foreground">This route includes a Channel crossing</p>
                  <p className="text-muted-foreground text-xs mt-1">{routeInfo.eurotunnel_note || "Eurotunnel LeShuttle Pet fee (£130–£250) is billed separately by our team."}</p>
                </div>
              </div>
            )}
          </motion.div>
        )}

        <div className="flex justify-between mt-10">
          <Button type="button" variant="outline" onClick={back} disabled={step === 0} data-testid="wizard-back-btn">
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button type="button" onClick={handleNext} data-testid="wizard-next-btn">
              Next <ArrowRight size={16} className="ml-2" />
            </Button>
          ) : (
            <Button type="button" onClick={submit} disabled={creating} data-testid="wizard-pay-btn">
              <CreditCard size={16} className="mr-2" /> {creating ? "Redirecting..." : `Pay £${total.toFixed(2)}`}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
